import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { debounce } from 'lodash';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import styled, { keyframes, css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { usePrivy, useLoginWithOAuth } from '@privy-io/react-auth';
import { Capacitor } from '@capacitor/core';
import LoadingSpinner from '../../components/LoadingSpinner';
import ApplePayButton from '../../components/ApplePayButton';
import TokenSelectInput from './TokenSelectInput';
import {
  CHECKOUT_TOKEN_ADDRESS_BY_NAME,
  getCheckoutTokenAddress,
} from '../../common/checkoutTokens';
import {
  selectTokenByNetworkAndAddress,
  selectTokenByNetworkAndSymbol,
} from '../../redux/slices/tokenSlice';
import { selectTokenBalance } from '../../redux/slices/walletSlice';
import {
  calculateApplePayFee,
  BALANCE_TOLERANCE,
} from '../../hooks/useApplePay';
import { formatUSD } from '../../common/currency';
import useApplePayCheckout from '../../hooks/useApplePayCheckout';
import { ApplePayWidget } from '../../components/ApplePayWidget';
import { useSolana } from '../../hooks/useSolana';
import { useGetCurrentUserProfileQuery } from '../../redux/api/authorized/auth';
import { openModal, closeModal } from '../../redux/slices/modals';
import { useTokenSearch } from '../../hooks/useTokenSearch';
import {
  sendEvent,
  EVENT_BUY_CHECKOUT_VIEWED,
  EVENT_BUY_CHECKOUT_AMOUNT_CHANGED,
  EVENT_BUY_CHECKOUT_APPLE_PAY_CLICKED,
  EVENT_APPLE_LOGIN_CLICKED,
  EVENT_BUY_CHECKOUT_COINBASE_APPLE_PAY_IFRAME_CLICKED,
  EVENT_BUY_CHECKOUT_APPLE_PAY_DEPOSIT_SUCCESS,
  EVENT_BUY_CHECKOUT_SWAP_STARTED,
  EVENT_BUY_CHECKOUT_SWAP_SUCCESS,
  EVENT_BUY_CHECKOUT_SWAP_FAILED,
  sendMetaEvent,
} from '../../common/analytics';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { ProgressBarLoading, ButtonContainer } from '../Gifts/SendingStatus';
import Button from '../../components/Button';
import { BodyLarge } from '../../components/Texts';

const CHECKOUT_NETWORK = 'solana';

// Amount options for instant buy (in USD)
const AMOUNTS = [25, 50, 100, 250];
const MIN_APPLE_PAY_AMOUNT = 5;
const MAX_APPLE_PAY_AMOUNT = 499;

const Content = styled.div`
  padding-bottom: 120px;
  margin-left: auto;
  margin-right: auto;
  padding-top: 20px;
`;

const pulseAnimation = keyframes`
  0% { opacity: 0.6; }
  50% { opacity: 1; }
  100% { opacity: 0.6; }
`;

const StatusWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  gap: 48px;
  min-height: 500px;
  @media ${PHONE_MEDIA_QUERY} {
    padding: 20px;
    gap: 24px;
    min-height: unset;
  }
`;

const StatusText = styled.h3`
  font-family: 'Inter', sans-serif;
  font-weight: 500;
  font-size: 20px;
  text-align: center;
  margin-top: 8px;
  color: ${({ theme, $isError }) =>
    $isError ? theme.status.error.primary : theme.text.title};
  ${({ $isLoading }) =>
    $isLoading &&
    css`
      animation: ${pulseAnimation} 2s infinite ease-in-out;
    `}
`;

const ErrorCircle = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: ${({ theme }) => theme.status.error.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.background.primary};
  font-size: 24px;
  font-weight: bold;
`;

const ErrorContact = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.5;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-top: 8px;
`;

const SuccessButton = styled(Button)`
  width: 100%;
  max-width: 200px;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: unset;
  }
`;

const TokenLoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  gap: 16px;
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.text.title};
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-bottom: 12px;
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
`;

const BigAmount = styled.div`
  font-size: 40px;
  font-weight: 700;
  color: ${({ theme }) => theme.text.title};
  text-align: center;
  margin-top: 24px;
`;

const AmountInputContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  min-height: 40px;
  position: relative;
  flex: 0 0 auto;
  max-width: 100%;
  padding-left: 14px;
  padding-right: 14px;
  overflow: visible;
  margin-top: 24px;
  cursor: text;
  @media ${PHONE_MEDIA_QUERY} {
    min-height: 44px;
    width: 100%;
  }
`;

const AmountStyledInput = styled.input`
  height: auto;
  min-height: 40px;
  background-color: transparent;
  display: inline-block;
  width: fit-content;
  flex: 0 0 auto;
  text-align: center;
  border: none;
  margin-left: -10px;
  margin-right: -10px;
  font-size: clamp(24px, 9vw, 64px);
  font-weight: 700;
  line-height: 1.2;
  padding: 0;
  color: ${({ theme }) => theme.text.title};
  outline: none;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(20px, 9vw, 64px);
    min-height: 36px;
  }
`;

const AmountInputPrefix = styled.span`
  display: flex;
  align-items: center;
  font-size: clamp(24px, 9vw, 64px);
  font-weight: 700;
  line-height: 1.2;
  color: ${({ theme }) => theme.text.title};
  flex-shrink: 0;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(20px, 9vw, 64px);
  }
`;

const AmountMeasureSpan = styled.span`
  position: absolute;
  left: -9999px;
  visibility: hidden;
  font-size: clamp(24px, 9vw, 64px);
  font-weight: 700;
  line-height: 1.2;
  white-space: nowrap;
  @media ${PHONE_MEDIA_QUERY} {
    font-size: clamp(20px, 9vw, 64px);
  }
`;

const TokenHint = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-top: 4px;
`;

const MinAmountError = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.status.error.primary};
  text-align: center;
  margin-top: 4px;
`;

const Section = styled.section`
  width: 100%;
`;

const TokenSelectorRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 16px;
  width: 100%;
  max-width: 600px;
`;

const InfoBlock = styled.div`
  padding: 16px 0px;
  border-radius: 12px;
`;

const InfoTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text.title};
  margin-bottom: 8px;
`;

const InfoText = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.text.secondary};
  line-height: 1.5;
  margin: 0;
`;

const AmountButton = styled.button`
  flex: 1;
  border-radius: 16px;
  border: 1px solid
    ${(props) =>
      props.$active
        ? `${props.theme.primary.primary}`
        : `${props.theme.border.primary}`};
  padding: 12px 12px;
  background-color: ${(props) =>
    props.$active
      ? `${props.theme.background.primary}`
      : `${props.theme.background.light}`};
  color: ${({ theme }) => theme.text.title};
  font-weight: 600;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  white-space: nowrap;

  @media (hover: hover) {
    &:hover:not(:disabled) {
      border-color: ${(props) =>
        props.$active
          ? `${props.theme.primary.primary}`
          : `${props.theme.text.disabled}`};
    }
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const AmountButtonContainer = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  margin-top: 16px;
`;

const PaySection = styled.div`
  padding: 50px 0px 20px 0px;
  position: fixed;
  display: flex;
  justify-content: center;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
  background: linear-gradient(
    to top,
    ${({ theme }) => theme.background.primary} 0%,
    ${({ theme }) => theme.background.primary} 60%,
    transparent 100%
  );
`;

const InnerPaySection = styled.div`
  max-width: 600px;
  width: 100%;
  position: relative;
  @media ${PHONE_MEDIA_QUERY} {
    max-width: 95%;
  }
`;

const FeeText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.text.secondary};
  text-align: center;
  margin-top: 8px;
`;

const ContentContainer = styled.div`
  display: flex;
  width: 100%;
  max-width: 600px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const FeedbackMessage = styled.p`
  font-size: 14px;
  color: ${({ theme, $isError }) =>
    $isError ? theme.status.error.primary : theme.text.secondary};
  text-align: center;
  margin-top: 8px;
`;

const LoadingButtonContainer = styled.div`
  position: absolute;
  width: 100%;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  justify-content: center;
`;

export default function Buy() {
  const { t } = useTranslation();
  const { tokenAddress: tokenParam } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const checkoutTokenEntries = Object.entries(CHECKOUT_TOKEN_ADDRESS_BY_NAME);
  const mappedAddress = getCheckoutTokenAddress(tokenParam);
  const tokenAddress = mappedAddress || tokenParam;

  const token = useSelector((state) =>
    tokenAddress
      ? selectTokenByNetworkAndAddress(state, CHECKOUT_NETWORK, tokenAddress)
      : null,
  );
  const { displayTokens } = useTokenSearch();

  const tokenOptions = useMemo(() => {
    const decorated = displayTokens.map((tkn) => ({
      ...tkn,
      displayLabel: `Buy ${tkn.name || tkn.symbol || tkn.address}`,
    }));

    if (token && !decorated.some((t) => t.address === token.address)) {
      decorated.unshift({
        ...token,
        displayLabel: `Buy ${token.name || token.symbol || token.address}`,
      });
    }

    return decorated;
  }, [displayTokens, token]);

  const usdcToken = useSelector((state) =>
    selectTokenByNetworkAndSymbol(state, CHECKOUT_NETWORK, 'USDC'),
  );
  const usdcTokenBalance = useSelector((state) =>
    selectTokenBalance(CHECKOUT_NETWORK, usdcToken?.address ?? '')(state),
  );
  const { authenticated } = usePrivy();
  const { loading: oauthLoading, initOAuth } = useLoginWithOAuth();
  const { data: currentUser, isSuccess: isUserProfileFetched } =
    useGetCurrentUserProfileQuery(undefined, { skip: !authenticated });

  const {
    executeUltraSwap,
    toTokenAmount,
    parseSolanaError,
    refreshTokenBalances,
  } = useSolana();

  const hasVerifiedPhone = Boolean(
    currentUser?.phoneVerifiedAt ?? currentUser?.phone_verified_at,
  );

  const [loading, setLoading] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [feedbackIsError, setFeedbackIsError] = useState(true);
  const [pendingApplePayUsdAmount, setPendingApplePayUsdAmount] =
    useState(null);
  const [waitingForApplePayBalance, setWaitingForApplePayBalance] =
    useState(false);
  const [applePayIframeLoaded, setApplePayIframeLoaded] = useState(true);

  const [buyStatus, setBuyStatus] = useState('idle'); // 'idle' | 'processing' | 'success' | 'error'

  const hasOpenedPhoneVerificationRef = useRef(false);

  const {
    startCheckout: startApplePayCheckout,
    widgetProps: applePayWidgetProps,
    isStarting: applePayStarting,
    isProcessingPayment: applePayProcessing,
    reset: resetApplePayCheckout,
  } = useApplePayCheckout();
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const amountFromQueryOnMount = searchParams.get('amount');
  const paymentStarted = authenticated;

  const initialAmount =
    amountFromQueryOnMount && AMOUNTS.includes(Number(amountFromQueryOnMount))
      ? Number(amountFromQueryOnMount)
      : 50;

  const [amountInputValue, setAmountInputValue] = useState(() =>
    String(initialAmount),
  );
  const resolvedAmount = (() => {
    const parsed = parseFloat(amountInputValue);
    return Number.isFinite(parsed) ? parsed : 0;
  })();
  const [debouncedResolvedAmount, setDebouncedResolvedAmount] =
    useState(resolvedAmount);
  const debouncedSetAmountRef = useRef(
    debounce((value) => setDebouncedResolvedAmount(value), 500),
  );
  useEffect(() => {
    debouncedSetAmountRef.current(resolvedAmount);
    return () => debouncedSetAmountRef.current.cancel();
  }, [resolvedAmount]);
  const amountInputRef = useRef(null);
  const amountMeasureRef = useRef(null);
  // Initial width from initial value length so "$" isn't far from the number on first paint
  const [amountLabelWidth, setAmountLabelWidth] = useState(() => {
    const len = String(initialAmount).length;
    return Math.min(140, Math.max(50, len * 28));
  });

  useLayoutEffect(() => {
    const el = amountMeasureRef?.current;
    if (!el) return;
    const measure = () => {
      const w = el.offsetWidth || 0;
      if (w > 0) setAmountLabelWidth(w);
    };
    measure();
    // Re-measure after layout/paint in case fonts or off-screen span weren't ready on first run
    const id = requestAnimationFrame(measure);
    return () => cancelAnimationFrame(id);
  }, [amountInputValue]);

  // Sync URL when amount changes (e.g. for OAuth return)
  useEffect(() => {
    if (!Number.isFinite(resolvedAmount) || resolvedAmount <= 0) return;
    const current = searchParams.get('amount');
    const next = String(resolvedAmount);
    if (current === next) return;
    setSearchParams(
      (prev) => {
        const nextParams = new URLSearchParams(prev);
        nextParams.set('amount', next);
        return nextParams;
      },
      { replace: true },
    );
  }, [resolvedAmount]);

  const isWeb = !Capacitor.isNativePlatform();
  const applePayAutoStartedRef = useRef(false);
  const debouncedAmountRef = useRef(undefined);

  const handleTokenSelected = useCallback(
    (selectedToken) => {
      if (!selectedToken?.address) return;
      navigate(`/buy/${selectedToken.address}`, { replace: true });
    },
    [navigate],
  );

  const mappedKey =
    mappedAddress && tokenParam
      ? tokenParam.toLowerCase()
      : checkoutTokenEntries.find(
          ([, address]) => address === tokenAddress,
        )?.[0];

  useEffect(() => {
    sendEvent(EVENT_BUY_CHECKOUT_VIEWED, {
      token_name: mappedKey ?? tokenParam ?? undefined,
      source: 'buy',
    });
  }, [mappedKey, tokenParam]);

  // When user stops typing (debounced amount) and we already have an Apple Pay link, invalidate it so we get a new link for the new amount
  useEffect(() => {
    if (!applePayWidgetProps) return;
    if (waitingForApplePayBalance) return;
    const prevAmount = debouncedAmountRef.current;
    debouncedAmountRef.current = debouncedResolvedAmount;
    if (prevAmount !== undefined && prevAmount !== debouncedResolvedAmount) {
      resetApplePayCheckout();
      applePayAutoStartedRef.current = false;
      if (authenticated) {
        setApplePayIframeLoaded(false);
      }
    }
  }, [
    debouncedResolvedAmount,
    applePayWidgetProps,
    waitingForApplePayBalance,
    resetApplePayCheckout,
    authenticated,
  ]);

  useEffect(() => {
    if (!tokenAddress) {
      navigate('/', { replace: true });
      return;
    }
  }, [tokenAddress, navigate]);

  // Poll for balance updates while waiting for Apple Pay funds
  useEffect(() => {
    const intervalId = setInterval(() => {
      refreshTokenBalances({ force: true });
    }, 3000);
    return () => clearInterval(intervalId);
  }, [refreshTokenBalances]);

  const performBuySwap = useCallback(
    async (usdAmount) => {
      if (!token?.address || !usdcToken?.address || !usdAmount) return;
      setBuyStatus('processing');
      try {
        sendEvent(EVENT_BUY_CHECKOUT_SWAP_STARTED);
        const rawAmount = toTokenAmount(usdAmount, usdcToken.decimals);
        const response = await executeUltraSwap({
          inputMint: usdcToken.address,
          outputMint: token.address,
          amount: rawAmount,
        });

        if (!response) {
          throw new Error('Swap failed');
        }

        sendMetaEvent('Purchase', {
          content_ids: [token.address],
          content_type: 'product',
          content_name: token?.name,
          currency: 'USD',
          value: parseFloat(usdAmount) || 0,
          num_items: 1,
        });

        setBuyStatus('success');
        sendEvent(EVENT_BUY_CHECKOUT_SWAP_SUCCESS);
        setFeedback(null);
      } catch (err) {
        sendEvent(EVENT_BUY_CHECKOUT_SWAP_FAILED);
        setBuyStatus('error');
        setFeedbackIsError(true);
        setFeedback(parseSolanaError(err));
      }
    },
    [executeUltraSwap, parseSolanaError, toTokenAmount, token, usdcToken],
  );

  const startApplePayFlow = useCallback(() => {
    if (!resolvedAmount || resolvedAmount < MIN_APPLE_PAY_AMOUNT) {
      setFeedbackIsError(true);
      setFeedback(
        t('checkoutPage.applePayMinAmount', { amount: MIN_APPLE_PAY_AMOUNT }),
      );
      return;
    }
    if (resolvedAmount > MAX_APPLE_PAY_AMOUNT) {
      setFeedbackIsError(true);
      setFeedback(
        t('checkoutPage.applePayMaxAmount', { amount: MAX_APPLE_PAY_AMOUNT }),
      );
      return;
    }

    startApplePayCheckout(resolvedAmount, {
      onSuccess: () => {
        sendEvent(EVENT_BUY_CHECKOUT_APPLE_PAY_DEPOSIT_SUCCESS, {
          token_name: mappedKey ?? tokenParam ?? undefined,
          amount: resolvedAmount,
          source: 'buy',
        });
        setFeedback(t('checkoutPage.waitingForFunds'));
        setFeedbackIsError(false);
        setPendingApplePayUsdAmount(resolvedAmount);
        setWaitingForApplePayBalance(true);
      },
      onPhoneVerificationExpired: () => {
        openPhoneVerificationModal();
        setFeedback(null);
        setFeedbackIsError(true);
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      },
      onError: (err) => {
        setFeedbackIsError(true);
        setFeedback(
          err
            ? t('checkoutPage.applePayStartError')
            : t('checkoutPage.applePayPaymentError'),
        );
        resetApplePayCheckout();
        setPendingApplePayUsdAmount(null);
        setWaitingForApplePayBalance(false);
      },
      onAlert: (title, message, type) => {
        if (type === 'error') {
          setFeedbackIsError(true);
          setFeedback(
            `Apple Pay: ${title || message || t('checkoutPage.applePayPaymentError')}`,
          );
          resetApplePayCheckout();
          setPendingApplePayUsdAmount(null);
          setWaitingForApplePayBalance(false);
        } else if (type === 'info') {
          setFeedbackIsError(false);
          setFeedback(message || title || null);
        }
      },
    }).catch(() => {});
  }, [
    resolvedAmount,
    startApplePayCheckout,
    resetApplePayCheckout,
    t,
    mappedKey,
    tokenParam,
  ]);

  const openPhoneVerificationModal = useCallback(() => {
    dispatch(
      openModal({
        name: 'verifyPhoneNumber',
        props: {
          onContinue: () => {
            dispatch(closeModal());
            startApplePayFlow();
          },
        },
      }),
    );
  }, [dispatch, startApplePayFlow]);

  const handleApplePayPress = useCallback(() => {
    if (!authenticated) {
      sendEvent(EVENT_APPLE_LOGIN_CLICKED, {
        token_name: mappedKey ?? tokenParam ?? undefined,
        source: 'buy',
      });
      sendMetaEvent('AddToCart', {
        content_ids: [token?.address],
        content_type: 'product',
        content_name: token?.name,
        currency: 'USD',
        value: parseFloat(resolvedAmount) || 0,
        num_items: 1,
      });
      setLoading(true);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('paymentStarted', '1');
          next.set('amount', String(resolvedAmount));
          return next;
        },
        { replace: true },
      );
      initOAuth({ provider: 'apple' });
      new Promise((resolve) => setTimeout(resolve, 5000)).finally(() =>
        setLoading(false),
      );
      return;
    }

    sendEvent(EVENT_BUY_CHECKOUT_APPLE_PAY_CLICKED, {
      token_name: mappedKey ?? tokenParam ?? undefined,
      source: 'buy',
    });

    if (isUserProfileFetched && !hasVerifiedPhone) {
      openPhoneVerificationModal();
      return;
    }

    if (!isUserProfileFetched) return;

    startApplePayFlow();
  }, [
    authenticated,
    isUserProfileFetched,
    hasVerifiedPhone,
    initOAuth,
    setSearchParams,
    startApplePayFlow,
    resolvedAmount,
    openPhoneVerificationModal,
    mappedKey,
    tokenParam,
    token,
  ]);

  // When payment started and authenticated but phone not verified: open phone verification modal (only after user profile is fetched)
  useEffect(() => {
    if (!paymentStarted || !authenticated || !isUserProfileFetched) {
      if (!paymentStarted) hasOpenedPhoneVerificationRef.current = false;
      return;
    }
    if (hasVerifiedPhone) return;
    if (hasOpenedPhoneVerificationRef.current) return;
    hasOpenedPhoneVerificationRef.current = true;
    openPhoneVerificationModal();
  }, [
    paymentStarted,
    authenticated,
    isUserProfileFetched,
    hasVerifiedPhone,
    openPhoneVerificationModal,
  ]);

  // Auto-start checkout when paymentStarted + authenticated + hasVerifiedPhone (e.g. returned from OAuth), using debounced amount
  useEffect(() => {
    if (!paymentStarted || !authenticated || !hasVerifiedPhone) return;
    if (applePayWidgetProps || applePayStarting) return;
    if (applePayAutoStartedRef.current) return;
    if (debouncedResolvedAmount < MIN_APPLE_PAY_AMOUNT) return;
    if (debouncedResolvedAmount > MAX_APPLE_PAY_AMOUNT) return;

    applePayAutoStartedRef.current = true;
    startApplePayFlow();
  }, [
    paymentStarted,
    authenticated,
    hasVerifiedPhone,
    applePayWidgetProps,
    applePayStarting,
    debouncedResolvedAmount,
    startApplePayFlow,
  ]);

  // Wait for Apple Pay funds, then perform swap from USDC to selected token
  useEffect(() => {
    if (!waitingForApplePayBalance || pendingApplePayUsdAmount == null) return;

    const numericBalance = parseFloat(usdcTokenBalance ?? '0');
    if (Number.isNaN(numericBalance)) return;

    const requiredBalance = Math.max(
      0,
      pendingApplePayUsdAmount - BALANCE_TOLERANCE,
    );
    if (numericBalance >= requiredBalance) {
      const amountToUse = pendingApplePayUsdAmount;
      setWaitingForApplePayBalance(false);
      setPendingApplePayUsdAmount(null);
      setFeedback(t('checkoutPage.paymentProcessing'));
      setFeedbackIsError(false);
      resetApplePayCheckout();
      performBuySwap(amountToUse).catch((err) => {
        setBuyStatus('error');
        setFeedbackIsError(true);
        setFeedback(parseSolanaError(err));
      });
    }
  }, [
    waitingForApplePayBalance,
    pendingApplePayUsdAmount,
    usdcTokenBalance,
    performBuySwap,
    resetApplePayCheckout,
    parseSolanaError,
    t,
  ]);

  useEffect(() => {
    if (!applePayWidgetProps) {
      setApplePayIframeLoaded(false);
    }
  }, [applePayWidgetProps]);

  useEffect(() => {
    return () => {
      resetApplePayCheckout();
      setPendingApplePayUsdAmount(null);
      setWaitingForApplePayBalance(false);
    };
  }, [resetApplePayCheckout]);

  if (!tokenAddress) {
    return null;
  }

  if (!token) {
    return (
      <Content>
        <TokenLoadingContainer>
          <LoadingSpinner />
          <Subtitle as='p'>{t('common.loading')}</Subtitle>
        </TokenLoadingContainer>
      </Content>
    );
  }

  const tokenLabel =
    mappedKey === 'yield'
      ? 'High interest'
      : token.name || token.symbol || mappedKey || tokenAddress;

  const applePayFee = calculateApplePayFee(resolvedAmount);

  // Success or processing state: inline animation, text and button
  if (buyStatus === 'processing' || buyStatus === 'success') {
    const isSuccess = buyStatus === 'success';
    return (
      <Content>
        <StatusWrapper>
          <ProgressBarLoading isSuccess={isSuccess} />
          <StatusText $isLoading={!isSuccess}>
            {isSuccess
              ? t('buyPage.successTitle', { tokenLabel })
              : t('buyPage.loadingTitle', { tokenLabel })}
          </StatusText>
          {isSuccess && (
            <ButtonContainer>
              <SuccessButton
                variant='secondaryLight'
                fullWidth={isMobile}
                onClick={() => {
                  dispatch(closeModal());
                  navigate('/app/home');
                }}
              >
                {t('buyPage.seeAssetButton')}
              </SuccessButton>
            </ButtonContainer>
          )}
        </StatusWrapper>
      </Content>
    );
  }

  // Error state: problem buying tokens, contact support
  if (buyStatus === 'error') {
    return (
      <Content>
        <StatusWrapper>
          <ErrorCircle>✗</ErrorCircle>
          <div>
            <StatusText $isError>{t('buyPage.errorTitle')}</StatusText>
            <ErrorContact>{t('buyPage.errorContact')}</ErrorContact>
          </div>
          <ButtonContainer>
            <SuccessButton
              variant='secondaryLight'
              fullWidth={isMobile}
              onClick={() => setBuyStatus('idle')}
            >
              {t('buyPage.tryAgainButton')}
            </SuccessButton>
          </ButtonContainer>
        </StatusWrapper>
      </Content>
    );
  }

  return (
    <Content>
      <ContentContainer>
        <div>
          <Title>{t('buyPage.title')}</Title>
          <Subtitle>
            {t('buyPage.subtitle', {
              tokenLabel,
            })}
          </Subtitle>
        </div>
        <TokenSelectorRow>
          <TokenSelectInput
            label={t('selectTokenModal.selectTokenToBuy')}
            value={token?.symbol}
            disabled={false}
            options={tokenOptions}
            onTokenSelected={handleTokenSelected}
          />
        </TokenSelectorRow>

        <Section style={{ marginTop: 8 }}>
          <AmountInputContainer onClick={() => amountInputRef.current?.focus()}>
            <AmountInputPrefix>$</AmountInputPrefix>
            <AmountStyledInput
              ref={amountInputRef}
              type='text'
              inputMode='decimal'
              enterKeyHint='done'
              aria-label={t('buyPage.amountLabel', {
                defaultValue: 'Amount in USD',
              })}
              value={amountInputValue}
              style={{
                maxWidth: `${
                  amountInputValue === ''
                    ? 24
                    : Math.max(amountLabelWidth + 24, 48)
                }px`,
              }}
              onChange={(e) => {
                const raw = e.target.value;
                const sanitized = raw
                  .replace(/[^0-9.]/g, '')
                  .replace(
                    /^(\d*\.)(.*)/,
                    (_, a, b) => a + b.replace(/\./g, ''),
                  );
                setAmountInputValue(sanitized);
                const num = parseFloat(sanitized);
                if (Number.isFinite(num)) {
                  sendEvent(EVENT_BUY_CHECKOUT_AMOUNT_CHANGED, {
                    amount: num,
                    token_name: mappedKey ?? tokenParam ?? undefined,
                    source: 'buy',
                  });
                }
              }}
            />
            <AmountMeasureSpan ref={amountMeasureRef}>
              {amountInputValue || '0'}
            </AmountMeasureSpan>
          </AmountInputContainer>
          <BodyLarge style={{ textAlign: 'center', marginTop: 2 }}>
            {`${parseFloat(
              (resolvedAmount / token?.usdPrice).toFixed(token?.decimals ?? 9),
            )} ${token?.symbol}`}
          </BodyLarge>
          {resolvedAmount > 0 && resolvedAmount < MIN_APPLE_PAY_AMOUNT && (
            <MinAmountError>
              {t('checkoutPage.applePayMinAmount', {
                amount: MIN_APPLE_PAY_AMOUNT,
              })}
            </MinAmountError>
          )}
          {resolvedAmount > MAX_APPLE_PAY_AMOUNT && (
            <MinAmountError>
              {t('checkoutPage.applePayMaxAmount', {
                amount: MAX_APPLE_PAY_AMOUNT,
              })}
            </MinAmountError>
          )}
          <AmountButtonContainer>
            {AMOUNTS.map((value) => (
              <AmountButton
                key={value}
                $active={resolvedAmount === value}
                onClick={() => {
                  setAmountInputValue(String(value));
                  setDebouncedResolvedAmount(value);
                  debouncedSetAmountRef.current.cancel();
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.set('amount', String(value));
                      return next;
                    },
                    { replace: true },
                  );
                  sendEvent(EVENT_BUY_CHECKOUT_AMOUNT_CHANGED, {
                    amount: value,
                    token_name: mappedKey ?? tokenParam ?? undefined,
                    source: 'buy',
                  });
                }}
              >
                ${value}
              </AmountButton>
            ))}
          </AmountButtonContainer>
        </Section>

        <Section style={{ marginTop: '20px' }}>
          <InfoBlock>
            <InfoTitle>{t('buyPage.howItWorksTitle')}</InfoTitle>
            <InfoText>
              {t('buyPage.howItWorksDescription', {
                tokenLabel,
              })}
            </InfoText>
          </InfoBlock>
          <InfoBlock>
            <InfoTitle>{t('buyPage.noWalletTitle')}</InfoTitle>
            <InfoText>{t('buyPage.noWalletDescription')}</InfoText>
          </InfoBlock>
        </Section>

        <PaySection>
          <InnerPaySection>
            {authenticated && !applePayIframeLoaded && isMobile && (
              <>
                <LoadingButtonContainer>
                  <ApplePayButton
                    onClick={() => {}}
                    loading={true}
                    disabled={true}
                  />
                </LoadingButtonContainer>
              </>
            )}
            {applePayWidgetProps ? (
              <>
                <ApplePayWidget
                  {...applePayWidgetProps}
                  onLoad={async () => {
                    await new Promise((resolve) => setTimeout(resolve, 3000));
                    setApplePayIframeLoaded(true);
                  }}
                  onCoinbaseApplePayInteraction={() =>
                    sendEvent(
                      EVENT_BUY_CHECKOUT_COINBASE_APPLE_PAY_IFRAME_CLICKED,
                      {
                        token_name: mappedKey ?? tokenParam ?? undefined,
                        source: 'buy',
                      },
                    )
                  }
                  onPaymentCancelled={() => {
                    // No-op for buy flow
                  }}
                />
                {!isWeb && (
                  <ApplePayButton
                    onClick={handleApplePayPress}
                    loading={applePayProcessing}
                    disabled={
                      applePayProcessing ||
                      resolvedAmount < MIN_APPLE_PAY_AMOUNT ||
                      resolvedAmount > MAX_APPLE_PAY_AMOUNT
                    }
                  />
                )}
              </>
            ) : isWeb && hasVerifiedPhone && authenticated ? (
              (applePayStarting || applePayProcessing) && (
                <ApplePayButton
                  onClick={() => {}}
                  loading={true}
                  disabled={true}
                />
              )
            ) : (
              <ApplePayButton
                onClick={handleApplePayPress}
                loading={
                  oauthLoading ||
                  loading ||
                  applePayStarting ||
                  applePayProcessing ||
                  waitingForApplePayBalance
                }
                disabled={
                  waitingForApplePayBalance ||
                  resolvedAmount < MIN_APPLE_PAY_AMOUNT ||
                  resolvedAmount > MAX_APPLE_PAY_AMOUNT
                }
              />
            )}
            <FeeText>
              {t('checkoutPage.applePayProcessingFee')} {formatUSD(applePayFee)}
            </FeeText>
            {feedback && (
              <FeedbackMessage $isError={feedbackIsError}>
                {feedback}
              </FeedbackMessage>
            )}
          </InnerPaySection>
        </PaySection>
      </ContentContainer>
    </Content>
  );
}
